/**
 * Every number, date and unit the interface renders is formatted here.
 *
 * The reason this file exists: "12.4 km" written inline in nine components
 * becomes nine subtly different answers the first time someone rounds
 * differently. One formatter per concept, used everywhere.
 */

const NBSP = ' '; // narrow no-break space — keeps "62 km/h" from wrapping

/**
 * 1284 → "1,284" · 12900 → "12.9K" · 4200000 → "4.2M" · 2.8 → "2.8"
 *
 * Small values keep a decimal. Rounding 2.8 km to "3 km" in a headline while
 * the table beside it says 2.8 makes the page look like it cannot add up,
 * and that is the fastest way to lose a reader's trust in every other number
 * on the screen.
 */
export function compactNumber(value, { decimals = 1 } = {}) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${trim(n / 1_000_000, decimals)}M`;
  if (abs >= 10_000) return `${trim(n / 1000, decimals)}K`;
  if (abs >= 100 || Number.isInteger(n)) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export function number(value, decimals = 0) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function trim(n, decimals) {
  return Number(n.toFixed(decimals)).toString();
}

export function speed(kph) {
  if (kph === null || kph === undefined) return '—';
  return `${Math.round(kph)}${NBSP}km/h`;
}

export function distance(km) {
  if (km === null || km === undefined) return '—';
  const n = Number(km);
  if (n < 1) return `${Math.round(n * 1000)}${NBSP}m`;
  return `${number(n, n < 100 ? 1 : 0)}${NBSP}km`;
}

export function temperature(c) {
  return c === null || c === undefined ? '—' : `${number(c, 1)}${NBSP}°C`;
}

export function percent(value, decimals = 0) {
  return value === null || value === undefined ? '—' : `${number(value, decimals)}%`;
}

/** GSM CSQ is 0–31; showing the raw number alone means nothing to most people. */
export function signal(csq) {
  if (csq === null || csq === undefined) return '—';
  const bars = csq >= 20 ? 'excellent' : csq >= 15 ? 'good' : csq >= 10 ? 'fair' : 'weak';
  return `${csq}/31 · ${bars}`;
}

export function coordinates(lat, lon) {
  if (lat === null || lon === null || lat === undefined || lon === undefined) return '—';
  return `${Number(lat).toFixed(5)}, ${Number(lon).toFixed(5)}`;
}

/** Seconds → "2h 14m" · "45m" · "38s" */
export function duration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s}s`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) return rest ? `${hours}h ${rest}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

export function dateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function time(value) {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function date(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * "just now" · "4m ago" · "2h ago" · "3d ago"
 * Used wherever last-seen matters, which is most of this product.
 */
export function relativeTime(value) {
  if (!value) return 'never';
  const ms = Date.now() - new Date(value).getTime();
  if (ms < 0) return 'just now';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 45) return 'just now';
  if (seconds < 90) return '1m ago';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date(value);
}

/** Masks an identifier for display: 869000000000001 → 8690…0001 */
export function maskId(value, keepStart = 4, keepEnd = 4) {
  if (!value) return '—';
  const s = String(value);
  if (s.length <= keepStart + keepEnd) return s;
  return `${s.slice(0, keepStart)}…${s.slice(-keepEnd)}`;
}

export function initials(name) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}
